import { useRef, useState } from "react";

import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import { NavLink, useNavigate } from "react-router-dom";

import { Button } from "src/base-components/Button";
import { TitleEditor } from "src/base-components/TitleEditor";
import { FeatureQueryCreate } from "src/clients/features-control";
import { Icon } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { FeatureCatalogueWrapper } from "src/featureCatalogue/FeatureCatalogue";
import {
  FeatureQueryForm,
  FeatureQueryFormRef,
} from "src/featureCatalogue/Queries/Create/FeatureQueryForm";
import type { QueryFormValues } from "src/featureCatalogue/Queries/Create/schema";
import { useCreateFeatureQuery } from "src/featureCatalogue/queries";
import { PageHeader } from "src/layout/PageHeader";
import { DashboardPageParams, getUrlToFeaturesPage } from "src/router/urls";
import { errorMessage } from "src/utils/stringUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const ManageFeatureQueryPage = () => {
  const [title, setTitle] = useState("Untitled Feature Query");
  const [isFormValid, setIsFormValid] = useState(false);
  const formRef = useRef<FeatureQueryFormRef>(null);
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const navigate = useNavigate();
  const createFeatureQueryMutation = useCreateFeatureQuery({
    invalidateFeatures: true,
  });

  const handleSubmit = async (data: QueryFormValues) => {
    try {
      const payload: FeatureQueryCreate = {
        query: data.query,
        query_name: title,
        columns: data.columns,
        window_length: data.window_length,
        window_hop: data.window_hop,
      };
      await createFeatureQueryMutation.mutateAsync(payload);
      toastActions.success({
        title: "Feature query created",
        description: "Your feature query has been created successfully",
      });
      navigate(getUrlToFeaturesPage(orgId, wsId));
    } catch (error) {
      const message =
        errorMessage(error) ??
        "An unexpected error occurred while creating the feature query";
      toastActions.failure({
        title: "Error creating feature query",
        description: message,
      });
    }
  };

  const isDeployDisabled =
    !isFormValid || !title.trim() || createFeatureQueryMutation.isPending;

  return (
    <FeatureCatalogueWrapper
      breadcrumbs={[
        {
          key: "back-to-queries",
          breadcrumb: (
            <PageHeader.Breadcrumb hideIcon>
              <NavLink
                data-loc="back-to-queries"
                to={getUrlToFeaturesPage(orgId, wsId)}
              >
                <Icon icon={faArrowLeftLong} size="xs" />
              </NavLink>
              <TitleEditor
                className="font-inter-semibold-13px"
                dataLoc="title-editor"
                value={title}
                autofocus
                onSubmit={setTitle}
              />
            </PageHeader.Breadcrumb>
          ),
        },
      ]}
      headerSideContent={
        <div className="flex gap-x-3">
          <Button
            dataLoc="deploy-button"
            disabled={isDeployDisabled}
            loading={createFeatureQueryMutation.isPending}
            onClick={() => formRef.current?.submit()}
          >
            Deploy
          </Button>
        </div>
      }
      showSubHeader={false}
    >
      <div className="h-full rounded-lg border border-gray-200 bg-white">
        <FeatureQueryForm
          ref={formRef}
          onSubmit={handleSubmit}
          onValidityChange={setIsFormValid}
        />
      </div>
    </FeatureCatalogueWrapper>
  );
};
